import React, { useState } from 'react';
import { 
  Alert, 
  AlertTitle, 
  Box,
  Typography,
  IconButton
} from '@mui/material';
import PropTypes from 'prop-types';
import ErrorDialog from './ErrorDialog';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Frozen module-level singletons for empty default props (#943,
// react/no-object-type-as-default-prop). A `= []` / `= {}` default builds a NEW
// value on every render, so a memoised child or an effect dependency sees a change
// that never happened.
const EMPTY_OBJECT = Object.freeze({});

/**
 * A component for displaying a simple error alert that can be clicked
 * to show more details in a dialog
 */
const ErrorAlert = ({ error, onClose, severity = "error", sx = EMPTY_OBJECT }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Default error object if none provided
  const errorData = error || {
    message: 'An unknown error occurred',
    details: 'No additional details available',
    isRateLimit: false,
    statusCode: 500,
    timestamp: new Date().toISOString()
  };
  
  // Set severity based on error type
  const alertSeverity = errorData.isRateLimit ? "warning" : severity;
  
  // Get title based on error type or status code
  const getAlertTitle = () => {
    if (errorData.isRateLimit) {
      return "Rate Limit Reached";
    }

    // #1059: a classified provider failure carries its own explanation in
    // `message`. Without this the 503 it reports falls through to "Server
    // Error", which points the user at the wrong system.
    if (errorData.isProviderUnavailable) {
      return "AI Assistant Unavailable";
    }
    
    if (errorData.statusCode >= 500) {
      return "Server Error";
    } else if (errorData.statusCode >= 400) {
      return "Request Error";
    } else if (errorData.statusCode === 401) {
      return "Authentication Error";
    } else if (errorData.statusCode === 403) {
      return "Permission Denied";
    }
    
    return "Error";
  };
  
  // Get user-friendly guidance for common errors
  const getErrorGuidance = () => {
    // #1059: for a classified provider failure the `message` already says what
    // is wrong AND what the user can do, so a second generic line underneath it
    // only contradicts the first.
    if (errorData.isProviderUnavailable) {
      return null;
    }

    if (errorData.isRateLimit) {
      if (Number.isFinite(errorData.retryAfter) && errorData.retryAfter > 0) {
        const s = errorData.retryAfter;
        return `Please wait about ${s} second${s === 1 ? '' : 's'} and try again, or switch to a different AI model.`;
      }
      return "Please wait a moment and try again, or switch to a different AI model.";
    }
    
    if (errorData.statusCode === 404) {
      return "The requested resource could not be found. It may have been moved or deleted.";
    }
    
    if (errorData.statusCode >= 500) {
      return "There was a problem with the server. Please try again later.";
    }
    
    if (errorData.message?.toLowerCase().includes('network') || 
        errorData.details?.toLowerCase().includes('network')) {
      return "Check your internet connection and try again.";
    }
    
    return null;
  };
  
  const guidance = getErrorGuidance();
  
  return (
    <>
      <Alert 
        severity={alertSeverity} 
        action={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="view details"
              color="inherit"
              size="small"
              onClick={() => setDialogOpen(true)}
              sx={{ mr: 0.5 }}
              title="View details"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={onClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        }
        sx={{ 
          width: '100%',
          '& .MuiAlert-message': { 
            width: '100%'
          },
          ...sx 
        }}
      >
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column'
        }}>
          <AlertTitle sx={{ mb: 0.5 }}>{getAlertTitle()}</AlertTitle>
          
          <Box sx={{
            display: 'flex', 
            flexDirection: 'column',
            width: '100%',
            mt: 0.5
          }}>
            <Typography variant="body2">
              {errorData.message}
            </Typography>
            
            {guidance && (
              <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.85rem' }}>
                {guidance}
              </Typography>
            )}
          </Box>
        </Box>
      </Alert>
      
      <ErrorDialog 
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        error={errorData}
        title={getAlertTitle()}
      />
    </>
  );
};

ErrorAlert.propTypes = {
  error: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  sx: PropTypes.object
};

export default ErrorAlert;